/*
 * ApneaMed patient intake - cart page gate.
 *
 * Responsibilities:
 *   1. Validate the Patient Information sheet in the browser (friendly errors).
 *   2. POST it straight to the intake service. Shopify never sees the PHI.
 *   3. Store only the returned opaque id as a cart attribute.
 *   4. Keep every route to checkout closed until that attribute exists.
 *
 * The Liquid snippet already renders the gated state server-side; this script
 * is the second line of defence for anything the customer can click.
 */
(function () {
  'use strict';

  var root = document.getElementById('PatientIntake');
  var cartForm = document.querySelector('form.cart[action="/cart"], form.cart');

  var US_STATES = ('AL AK AZ AR CA CO CT DE FL GA HI ID IL IN IA KS KY LA ME MD MA MI MN MS ' +
    'MO MT NE NV NH NJ NM NY NC ND OH OK OR PA RI SC SD TN TX UT VT VA WA WV WI WY DC PR VI ' +
    'GU AS MP').split(' ');

  /* ----------------------------------------------------------- gating --- */

  function isComplete() {
    return !root || root.getAttribute('data-intake-complete') === 'true';
  }

  function lockCheckout() {
    if (isComplete()) return;

    // The theme's own submit button.
    var checkoutBtns = document.querySelectorAll('[name="checkout"]');
    Array.prototype.forEach.call(checkoutBtns, function (btn) {
      btn.disabled = true;
      btn.setAttribute('aria-disabled', 'true');
      btn.title = 'Complete the Patient Information sheet to continue';
      btn.classList.add('is-intake-locked');
    });

    // Shop Pay / PayPal / Google Pay bypass the cart form entirely, so they have
    // to go away rather than merely be disabled.
    var accel = document.querySelector('.cart__additional_checkout, .additional-checkout-buttons');
    if (accel) accel.setAttribute('hidden', 'hidden');

    // Last resort: someone re-enables the button in devtools.
    if (cartForm && !cartForm.__intakeGuard) {
      cartForm.__intakeGuard = true;
      cartForm.addEventListener('submit', function (evt) {
        var submitter = evt.submitter || document.activeElement;
        var isCheckout = submitter && submitter.getAttribute &&
          submitter.getAttribute('name') === 'checkout';
        if (isCheckout && !isComplete()) {
          evt.preventDefault();
          if (root) root.scrollIntoView({ behavior: 'smooth', block: 'start' });
          setStatus('Please complete the Patient Information sheet first.', 'error');
        }
      });
    }
  }

  function unlockCheckout() {
    var checkoutBtns = document.querySelectorAll('[name="checkout"]');
    Array.prototype.forEach.call(checkoutBtns, function (btn) {
      btn.disabled = false;
      btn.removeAttribute('aria-disabled');
      btn.removeAttribute('title');
      btn.classList.remove('is-intake-locked');
    });
    var accel = document.querySelector('.cart__additional_checkout, .additional-checkout-buttons');
    if (accel) accel.removeAttribute('hidden');
  }

  /* -------------------------------------------------------- utilities --- */

  function val(name) {
    var el = root.querySelector('[name="' + name + '"]');
    return el ? String(el.value).trim() : '';
  }

  function setStatus(message, kind) {
    var el = root && root.querySelector('[data-intake-status]');
    if (!el) return;
    el.textContent = message || '';
    el.className = 'patient-intake__status' + (kind ? ' is-' + kind : '');
  }

  function clearErrors() {
    Array.prototype.forEach.call(
      root.querySelectorAll('[data-error-for]'),
      function (el) { el.textContent = ''; }
    );
    Array.prototype.forEach.call(
      root.querySelectorAll('.has-error'),
      function (el) { el.classList.remove('has-error'); }
    );
    var box = root.querySelector('[data-intake-formerror]');
    if (box) { box.hidden = true; box.textContent = ''; }
  }

  function showError(field, message) {
    var slot = root.querySelector('[data-error-for="' + field + '"]');
    if (slot) slot.textContent = message;
    var input = root.querySelector('[name="' + field + '"]');
    if (input) input.classList.add('has-error');
  }

  function showFormError(message) {
    var box = root.querySelector('[data-intake-formerror]');
    if (!box) return;
    box.textContent = message;
    box.hidden = false;
  }

  /* ------------------------------------------------------- validation --- */

  function validate() {
    var errors = {};
    var required = {
      first_name: 'Enter the patient first name',
      last_name: 'Enter the patient last name',
      address1: 'Enter the shipping street address',
      city: 'Enter the city',
      symptoms: 'Describe the symptoms',
      medical_history: 'Enter the medical history, or "None"'
    };
    Object.keys(required).forEach(function (k) {
      if (!val(k)) errors[k] = required[k];
    });

    var state = val('state').toUpperCase();
    if (!state) errors.state = 'Enter a state';
    else if (US_STATES.indexOf(state) === -1) errors.state = 'Use a 2-letter state code';

    var zip = val('zip_code');
    if (!zip) errors.zip_code = 'Enter a ZIP code';
    else if (!/^\d{5}(-\d{4})?$/.test(zip)) errors.zip_code = 'Use 12345 or 12345-6789';

    var digits = val('phone').replace(/\D+/g, '');
    if (digits.length === 11 && digits.charAt(0) === '1') digits = digits.slice(1);
    if (!digits) errors.phone = 'Enter a phone number';
    else if (digits.length !== 10) errors.phone = 'Enter a 10-digit US phone number';

    var email = val('email');
    if (!email) errors.email = 'Enter an email address';
    else if (!/^[^@\s]+@[^@\s]+\.[^@\s]+$/.test(email)) errors.email = 'Check this email address';

    if (!val('sex')) errors.sex = 'Select one';

    var dob = val('date_of_birth');
    if (!dob) {
      errors.date_of_birth = 'Enter a date of birth';
    } else {
      var d = new Date(dob + 'T00:00:00');
      if (isNaN(d.getTime())) {
        errors.date_of_birth = 'Enter a valid date';
      } else if (d > new Date()) {
        errors.date_of_birth = 'Date of birth cannot be in the future';
      } else if (new Date().getFullYear() - d.getFullYear() > 120) {
        errors.date_of_birth = 'Check this date';
      }
    }

    var ft = parseInt(val('height_feet'), 10);
    if (isNaN(ft) || ft < 1 || ft > 8) errors.height_feet = 'Feet must be 1-8';
    var inches = parseInt(val('height_inches'), 10);
    if (isNaN(inches) || inches < 0 || inches > 11) errors.height_inches = 'Inches must be 0-11';

    var lbs = parseInt(val('weight_lbs'), 10);
    if (isNaN(lbs) || lbs < 20 || lbs > 999) errors.weight_lbs = 'Weight must be 20-999 lbs';

    return errors;
  }

  function payload(cartToken) {
    return {
      first_name: val('first_name'),
      last_name: val('last_name'),
      address1: val('address1'),
      address2: val('address2'),
      city: val('city'),
      state: val('state').toUpperCase(),
      zip_code: val('zip_code'),
      phone: val('phone'),
      email: val('email'),
      sex: val('sex'),
      date_of_birth: val('date_of_birth'),
      height_feet: parseInt(val('height_feet'), 10),
      height_inches: parseInt(val('height_inches'), 10),
      weight_lbs: parseInt(val('weight_lbs'), 10),
      symptoms: val('symptoms'),
      medical_history: val('medical_history'),
      cart_token: cartToken || null
    };
  }

  /* ----------------------------------------------------------- submit --- */

  function submit(button) {
    clearErrors();

    var errors = validate();
    var names = Object.keys(errors);
    if (names.length) {
      names.forEach(function (k) { showError(k, errors[k]); });
      showFormError('Please correct the ' + names.length +
        (names.length === 1 ? ' field' : ' fields') + ' marked below.');
      var first = root.querySelector('[name="' + names[0] + '"]');
      if (first) first.focus();
      return;
    }

    button.disabled = true;
    setStatus('Saving...', 'busy');

    var endpoint = root.getAttribute('data-intake-endpoint').replace(/\/+$/, '');

    fetch('/cart.js', { credentials: 'same-origin' })
      .then(function (r) { return r.ok ? r.json() : {}; })
      .catch(function () { return {}; })
      .then(function (cart) {
        return fetch(endpoint + '/api/intake', {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify(payload(cart && cart.token))
        });
      })
      .then(function (resp) {
        return resp.json().then(function (body) {
          return { status: resp.status, body: body };
        });
      })
      .then(function (res) {
        if (res.status === 422 && res.body && res.body.errors) {
          Object.keys(res.body.errors).forEach(function (k) {
            showError(k, res.body.errors[k]);
          });
          showFormError('Please correct the fields marked below.');
          setStatus('', '');
          button.disabled = false;
          return null;
        }
        if (res.status === 429) {
          throw new Error('Too many attempts. Please wait a few minutes and try again.');
        }
        if (res.status !== 200 || !res.body || !res.body.intake_id) {
          throw new Error('We could not save the patient information.');
        }
        return saveAttribute(res.body.intake_id);
      })
      .then(function (saved) {
        if (saved) window.location.reload();
      })
      .catch(function (err) {
        button.disabled = false;
        setStatus('', '');
        showFormError(
          (err && err.message ? err.message : 'Something went wrong.') +
          ' If this continues, please email orders@apneamed.org.'
        );
      });
  }

  function saveAttribute(intakeId) {
    return fetch('/cart/update.js', {
      method: 'POST',
      credentials: 'same-origin',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ attributes: { 'Patient Intake ID': intakeId } })
    }).then(function (r) {
      if (!r.ok) throw new Error('We saved your information but could not attach it to the cart.');
      return true;
    });
  }

  function reset() {
    fetch('/cart/update.js', {
      method: 'POST',
      credentials: 'same-origin',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ attributes: { 'Patient Intake ID': '' } })
    }).then(function () { window.location.reload(); });
  }

  /* ------------------------------------------------------------- boot --- */

  function boot() {
    if (!root) return;

    if (isComplete()) {
      unlockCheckout();
      var resetBtn = root.querySelector('[data-intake-reset]');
      if (resetBtn) resetBtn.addEventListener('click', reset);
      return;
    }

    lockCheckout();

    var submitBtn = root.querySelector('[data-intake-submit]');
    if (submitBtn) {
      submitBtn.addEventListener('click', function () { submit(submitBtn); });
    }

    // Clear a field's error as soon as the customer edits it.
    Array.prototype.forEach.call(
      root.querySelectorAll('[name]'),
      function (input) {
        input.addEventListener('input', function () {
          input.classList.remove('has-error');
          var slot = root.querySelector('[data-error-for="' + input.getAttribute('name') + '"]');
          if (slot) slot.textContent = '';
        });
      }
    );
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', boot);
  } else {
    boot();
  }

  // Apps on this theme re-render the cart asynchronously (the free-gift app in
  // particular). Re-assert the lock if our node is replaced.
  var observer = new MutationObserver(function () {
    var current = document.getElementById('PatientIntake');
    if (current && current !== root) {
      root = current;
      boot();
    } else if (!isComplete()) {
      lockCheckout();
    }
  });
  if (document.body) {
    observer.observe(document.body, { childList: true, subtree: true });
  }
})();
